import {
  type UseFormReturn,
  type FieldValues,
  type Path,
  type PathValue,
} from "react-hook-form";
import { z } from "zod";

/**
 * 最小値最大値を設定しない場合の関数
 * @param rangeErrorMessage 範囲開始値<範囲終了値となっていない場合のエラーメッセージ
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const makeRangeSchema = (rangeErrorMessage: string) => {
  return z
    .object({
      min: z.number().nullable(),
      max: z.number().nullable(),
    })
    .refine(
      (args) => {
        // min<=maxを確認。
        const { min, max } = args;
        if (min === null || max === null) return true;
        return min <= max;
      },
      {
        message: rangeErrorMessage,
      }
    );
};

/**
 * 最小値最大値を設定する場合の関数
 * @param min 設定可能な最小値
 * @param max 設定可能な最大値
 * @param rangeErrorMessage 範囲開始値<範囲終了値となっていない場合のエラーメッセージ
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const makeRangeSchemaWithRestrictMinMax = (
  min: number,
  max: number,
  rangeErrorMessage: string
) => {
  const minErrorMessage = `${min}以上の数値を入力してください`;
  const maxErrorMessage = `${max}以下の数値を入力してください`;

  /**
   * 最小値のチェック。
   * @param {string} value
   * @returns {boolean}
   */
  const checkMin = (value: number | null): boolean => {
    if (value === null) return true;
    return min <= value;
  };

  /**
   * 最大値のチェック。
   * @param {string} value
   * @returns {boolean}
   */
  const checkMax = (value: number | null): boolean => {
    if (value === null) return true;
    return max >= value;
  };

  return z
    .object({
      min: z
        .number()
        .nullable()
        .refine((value) => checkMin(value), minErrorMessage)
        .refine((value) => checkMax(value), maxErrorMessage),
      max: z
        .number()
        .nullable()
        .refine((value) => checkMin(value), minErrorMessage)
        .refine((value) => checkMax(value), maxErrorMessage),
    })
    .refine(
      (args) => {
        // min<=maxを確認。
        const { min, max } = args;
        if (min === null || max === null) return true;
        return min <= max;
      },
      {
        message: rangeErrorMessage,
      }
    );
};

/**
 * 再帰的にreact-hook-formのsetValueを使って値を更新する
 * @param form react-hook-formのUseFormReturnオブジェクト
 * @param basePath 基本パス
 * @param values 設定する値
 */
export const setValuesRecursively = <T extends FieldValues>(
  form: UseFormReturn<T>,
  basePath: string,
  values: Record<string, unknown>
): void => {
  for (const key in values) {
    const value = values[key];
    const path = `${basePath}.${key}`;

    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      setValuesRecursively(form, path, value as Record<string, unknown>);
    } else {
      form.setValue(path as Path<T>, value as PathValue<T, Path<T>>);
    }
  }
};

/**
 * 整数範囲フィールド用のスキーマ（最小値・最大値を指定）
 * @param min 最小値
 * @param max 最大値
 * @param rangeErrorMessage 範囲開始値<範囲終了値となっていない場合のエラーメッセージ
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const makeIntegerRangeSchema = (
  min: number,
  max: number,
  rangeErrorMessage: string
) => {
  return z
    .object({
      min: z
        .number()
        .nullable()
        .refine(
          (value) =>
            value === null || (Number.isInteger(value) && value >= min),
          `${min}以上の整数を入力してください`
        )
        .refine(
          (value) => value === null || value <= max,
          `${max}以下の値を入力してください`
        ),
      max: z
        .number()
        .nullable()
        .refine(
          (value) =>
            value === null || (Number.isInteger(value) && value >= min),
          `${min}以上の整数を入力してください`
        )
        .refine(
          (value) => value === null || value <= max,
          `${max}以下の値を入力してください`
        ),
    })
    .refine(
      (args) => {
        // min<=maxを確認。
        const { min, max } = args;
        if (min === null || max === null) return true;
        return min <= max;
      },
      {
        message: rangeErrorMessage,
      }
    );
};

/**
 * 面積範囲フィールド用のスキーマ
 * @param rangeErrorMessage 範囲開始値<範囲終了値となっていない場合のエラーメッセージ
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const makeAreaRangeSchema = (rangeErrorMessage: string) => {
  const AREA_MIN = 0;
  const AREA_MAX = 99999999999999;

  // NOTE: 面積として0は現実にあり得ないため「範囲指定なし」としてnullに正規化する。
  // 入力欄は0を空文字として表示する一方で内部の値は0のまま保持されるため、
  // 正規化しないと「以下に0、以上に100」で画面上は空に見えるのに
  // min <= max のエラーが出続けて解除できなくなる。
  // バックエンドも0をnullとして扱うため解釈を揃える。
  const normalizeZeroToNull = (value: number | null): number | null =>
    value === 0 ? null : value;

  return z
    .object({
      min: z
        .number()
        .nullable()
        .transform(normalizeZeroToNull)
        .refine(
          (value) => value === null || value >= AREA_MIN,
          `${AREA_MIN}以上の数値を入力してください`
        )
        .refine(
          (value) => value === null || value <= AREA_MAX,
          "値が大きすぎます"
        ),
      max: z
        .number()
        .nullable()
        .transform(normalizeZeroToNull)
        .refine(
          (value) => value === null || value >= AREA_MIN,
          `${AREA_MIN}以上の数値を入力してください`
        )
        .refine(
          (value) => value === null || value <= AREA_MAX,
          "値が大きすぎます"
        ),
    })
    .refine(
      (args) => {
        // min<=maxを確認。
        const { min, max } = args;
        if (min === null || max === null) return true;
        return min <= max;
      },
      {
        message: rangeErrorMessage,
      }
    );
};

/**
 * 大きな整数範囲フィールド用のスキーマ（bigint相当の範囲）
 * @param rangeErrorMessage 範囲開始値<範囲終了値となっていない場合のエラーメッセージ
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const makeBigIntRangeSchema = (rangeErrorMessage: string) => {
  const MIN = 0;
  const MAX = Number.MAX_SAFE_INTEGER;

  return z
    .object({
      min: z
        .number()
        .nullable()
        .refine(
          (value) => value === null || value >= MIN,
          `${MIN}以上の数値を入力してください`
        )
        .refine((value) => value === null || value <= MAX, "値が大きすぎます")
        .refine(
          (value) => value === null || Number.isInteger(value),
          "整数を入力してください"
        ),
      max: z
        .number()
        .nullable()
        .refine(
          (value) => value === null || value >= MIN,
          `${MIN}以上の数値を入力してください`
        )
        .refine((value) => value === null || value <= MAX, "値が大きすぎます")
        .refine(
          (value) => value === null || Number.isInteger(value),
          "整数を入力してください"
        ),
    })
    .refine(
      (args) => {
        // min<=maxを確認。
        const { min, max } = args;
        if (min === null || max === null) return true;
        return min <= max;
      },
      {
        message: rangeErrorMessage,
      }
    );
};

/**
 * 日付範囲フィールド用のスキーマ（nullable対応）
 * @param minDate 選択可能な最小日付
 * @param maxDateOrFactory 選択可能な最大日付、または最大日付を返す関数
 * @param rangeLimitErrorMessage 範囲外の値が入力された場合のエラーメッセージ
 * @param rangeOrderErrorMessage 開始日が終了日より後の場合のエラーメッセージ
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const makeNullableDateRangeSchema = (
  minDate: Date,
  maxDateOrFactory: Date | (() => Date),
  rangeLimitErrorMessage: string,
  rangeOrderErrorMessage: string
) => {
  const getMaxDate =
    typeof maxDateOrFactory === "function"
      ? maxDateOrFactory
      : () => maxDateOrFactory;

  return z
    .tuple([z.date().nullable(), z.date().nullable()])
    .superRefine(([startDate, endDate], ctx) => {
      const maxDate = getMaxDate();
      const isOutOfRange = (date: Date | null): boolean =>
        date !== null && (date < minDate || date > maxDate);

      if (isOutOfRange(startDate)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [0],
          message: rangeLimitErrorMessage,
        });
      }
      if (isOutOfRange(endDate)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [1],
          message: rangeLimitErrorMessage,
        });
      }
      if (startDate !== null && endDate !== null && startDate > endDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [0],
          message: rangeOrderErrorMessage,
        });
      }
    });
};
